import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Activity,
  RefreshCw,
  Eye,
  Info,
  Sparkles
} from 'lucide-react';
import {
  loggingApiService,
  SecurityDashboard,
  SecurityEvent,
  LexExplainRequest,
  LexSecurityExplainData,
  LexSecurityReference,
  LexSecurityTimeframe,
} from '../../../../services/loggingApiService';
import { LexExplainModal } from './LexExplainModal';

/**
 * Simplified Security Events Panel Component
 * 
 * Displays security dashboard with Object Structure categories
 */
export const SecurityEventsPanel: React.FC = () => {
  const [dashboard, setDashboard] = useState<SecurityDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeframe, setTimeframe] = useState<'24h' | '7d' | '30d'>('7d');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [showInfoModal, setShowInfoModal] = useState<string | null>(null);
  const [lexOpen, setLexOpen] = useState(false);
  const [lexLoading, setLexLoading] = useState(false);
  const [lexReferenceLoading, setLexReferenceLoading] = useState(false);
  const [lexError, setLexError] = useState<string | null>(null);
  const [lexTitle, setLexTitle] = useState<string>('Lex Explanation');
  const [lexSubtitle, setLexSubtitle] = useState<string | undefined>(undefined);
  const [lexExplanation, setLexExplanation] = useState<string | undefined>(undefined);
  const [lexProvider, setLexProvider] = useState<string | undefined>(undefined);
  const [lexModel, setLexModel] = useState<string | undefined>(undefined);
  const [lexReferences, setLexReferences] = useState<LexSecurityReference[] | undefined>(undefined);
  const [lexStack, setLexStack] = useState<Array<{
    title: string;
    subtitle?: string;
    explanation?: string;
    provider?: string;
    model?: string;
    references?: LexSecurityReference[];
  }>>([]);
  const [lastLexRequest, setLastLexRequest] = useState<LexExplainRequest | null>(null);
  const [lastSecurityOverviewTimeframe, setLastSecurityOverviewTimeframe] = useState<LexSecurityTimeframe | null>(null);
  const [lastSecurityReference, setLastSecurityReference] = useState<LexSecurityReference | null>(null);
  const [recentSecurityEvents, setRecentSecurityEvents] = useState<SecurityEvent[]>([]);
  const [recentSecurityEventsTotal, setRecentSecurityEventsTotal] = useState<number>(0);
  const [recentSecurityEventsError, setRecentSecurityEventsError] = useState<string | null>(null);
  const [expandedSecurityEventId, setExpandedSecurityEventId] = useState<string | null>(null);

  useEffect(() => {
    // Timeframe change is user-driven; show a real loading state for clarity.
    setDashboard(null);
    setRecentSecurityEvents([]);
    setRecentSecurityEventsTotal(0);
    setRecentSecurityEventsError(null);
    setExpandedSecurityEventId(null);
    loadDashboard({ mode: 'initial' });
  }, [timeframe]);

  // Add auto-refresh for real-time data
  useEffect(() => {
    const interval = setInterval(() => {
      // Avoid jarring refreshes while the user is reading a Lex explanation.
      if (lexOpen) return;
      // Avoid unnecessary work in background tabs.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      // Avoid overlapping refreshes.
      if (loading || refreshing) return;
      loadDashboard({ mode: 'refresh' });
    }, 60000); // 60-second refresh

    return () => clearInterval(interval);
  }, [lexOpen, loading, refreshing, timeframe]);

  const loadDashboard = async (opts?: { mode?: 'initial' | 'refresh' }) => {
    const mode = opts?.mode || 'refresh';
    try {
      if (mode === 'initial') setLoading(true);
      else setRefreshing(true);
      const now = new Date();
      const start = new Date(now);
      if (timeframe === '24h') start.setDate(start.getDate() - 1);
      else if (timeframe === '7d') start.setDate(start.getDate() - 7);
      else start.setDate(start.getDate() - 30);

      const [dashboardRes, eventsRes] = await Promise.allSettled([
        loggingApiService.getSecurityDashboard(timeframe),
        loggingApiService.getSecurityEvents({
          page: 1,
          limit: 10,
          startDate: start.toISOString(),
          endDate: now.toISOString(),
        }),
      ]);

      if (dashboardRes.status === 'fulfilled') {
        setDashboard(dashboardRes.value);
      } else {
        throw dashboardRes.reason;
      }

      if (eventsRes.status === 'fulfilled') {
        setRecentSecurityEvents(eventsRes.value.events || []);
        setRecentSecurityEventsTotal(eventsRes.value.total || 0);
        setRecentSecurityEventsError(null);
      } else {
        console.warn('Failed to load recent security events:', eventsRes.reason);
        setRecentSecurityEvents([]);
        setRecentSecurityEventsTotal(0);
        setRecentSecurityEventsError('Failed to load recent security events');
      }

      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load security dashboard:', error);
    } finally {
      if (mode === 'initial') setLoading(false);
      setRefreshing(false);
    }
  };

  const runLex = async (request: LexExplainRequest, opts?: { title?: string; subtitle?: string }) => {
    setLexTitle(opts?.title || 'Lex Explanation');
    setLexSubtitle(opts?.subtitle);
    setLexError(null);
    setLexExplanation(undefined);
    setLexProvider(undefined);
    setLexModel(undefined);
    setLexReferences(undefined);
    setLexStack([]);
    setLastSecurityOverviewTimeframe(null);
    setLastSecurityReference(null);
    setLexLoading(true);
    setLexOpen(true);
    setLastLexRequest(request);

    try {
      const res = await loggingApiService.explainWithLex(request);
      setLexExplanation(res.explanation);
      setLexProvider(res.provider);
      setLexModel(res.model);
    } catch (e) {
      setLexError(e instanceof Error ? e.message : 'Failed to generate Lex explanation');
    } finally {
      setLexLoading(false);
    }
  };

  const applyLexSecurityResponse = (res: LexSecurityExplainData) => {
    setLexExplanation(res.explanation);
    setLexProvider(res.provider);
    setLexModel(res.model);
    setLexReferences(Array.isArray(res.references) ? res.references : []);
  };

  const runLexSecurityOverview = async (tf: LexSecurityTimeframe) => {
    setLexTitle('Lex: Security Dashboard');
    setLexSubtitle(`Timeframe: ${tf}`);
    setLexError(null);
    setLexExplanation(undefined);
    setLexProvider(undefined);
    setLexModel(undefined);
    setLexReferences(undefined);
    setLexStack([]);
    setLexReferenceLoading(false);
    setLexLoading(true);
    setLexOpen(true);
    setLastSecurityOverviewTimeframe(tf);
    setLastSecurityReference(null);
    setLastLexRequest(null);

    try {
      const res = await loggingApiService.explainSecurityOverview({
        timeframe: tf,
        includeEvidence: true,
      });
      applyLexSecurityResponse(res);
    } catch (e) {
      setLexError(e instanceof Error ? e.message : 'Failed to generate Lex security explanation');
    } finally {
      setLexLoading(false);
    }
  };

  const runLexSecurityReference = async (ref: LexSecurityReference, opts?: { pushStack?: boolean }) => {
    const tf = (ref?.filters?.timeframe as LexSecurityTimeframe) || lastSecurityOverviewTimeframe || (timeframe as LexSecurityTimeframe);
    const pushStack = opts?.pushStack !== false;

    if (pushStack) {
      setLexStack((prev) => [
        ...prev,
        { title: lexTitle, subtitle: lexSubtitle, explanation: lexExplanation, provider: lexProvider, model: lexModel, references: lexReferences },
      ]);
    }

    setLexTitle(`Lex: ${ref.label}`);
    setLexSubtitle(`Timeframe: ${tf}`);
    setLexError(null);
    setLexExplanation(undefined);
    setLexProvider(undefined);
    setLexModel(undefined);
    setLexReferenceLoading(true);
    setLastSecurityReference(ref);

    try {
      const res = await loggingApiService.explainSecurityReference({
        timeframe: tf,
        reference: ref,
        maxItems: 25,
      });
      applyLexSecurityResponse(res);
    } catch (e) {
      setLexError(e instanceof Error ? e.message : 'Failed to load evidence explanation');
    } finally {
      setLexReferenceLoading(false);
    }
  };

  const handleLexBack = () => {
    setLexStack((prev) => {
      if (prev.length === 0) return prev;
      const next = prev.slice(0, -1);
      const last = prev[prev.length - 1];

      setLexTitle(last.title);
      setLexSubtitle(last.subtitle);
      setLexExplanation(last.explanation);
      setLexProvider(last.provider);
      setLexModel(last.model);
      setLexReferences(last.references);
      setLexError(null);
      setLastSecurityReference(null);

      return next;
    });
  };

  const handleLexRetry = () => {
    if (lexLoading || lexReferenceLoading) return;
    if (lastSecurityReference) {
      runLexSecurityReference(lastSecurityReference, { pushStack: false });
      return;
    }
    if (lastSecurityOverviewTimeframe) {
      runLexSecurityOverview(lastSecurityOverviewTimeframe);
      return;
    }
    if (lastLexRequest) {
      runLex(lastLexRequest, { title: lexTitle, subtitle: lexSubtitle });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'all_clear': return 'border-green-500 bg-green-50 dark:bg-green-900/20';
      case 'monitoring': return 'border-lex-accent bg-lex-accent-soft';
      case 'attention_needed': return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
      case 'critical': return 'border-red-500 bg-red-50 dark:bg-red-900/20';
      case 'degraded': return 'border-amber-500 bg-amber-50 dark:bg-amber-900/20';
      default: return 'border-gray-500 bg-gray-50 dark:bg-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'all_clear': return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'monitoring': return <Eye className="w-6 h-6 text-lex-accent" />;
      case 'attention_needed': return <AlertTriangle className="w-6 h-6 text-yellow-600" />;
      case 'critical': return <AlertTriangle className="w-6 h-6 text-red-600" />;
      case 'degraded': return <Info className="w-6 h-6 text-amber-600" />;
      default: return <Shield className="w-6 h-6 text-gray-600" />;
    }
  };

  const getCategoryStatusColor = (status: 'good' | 'warning' | 'critical') => {
    switch (status) {
      case 'good': return 'text-green-600 bg-green-100 border-green-200';
      case 'warning': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'critical': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getCategoryDescription = (key: string): string => {
    const descriptions: Record<string, string> = {
      authentication: 'Login attempts, session management, and access verification',
      dataAccess: 'Document views, downloads, and sensitive data access',
      activityMonitoring: 'Suspicious patterns, unusual behavior, and security violations',
      userManagement: 'User creation, role changes, and permission modifications',
      financial: 'Billing access, payment processing, and financial transactions'
    };
    return descriptions[key] || 'Security monitoring and event analysis';
  };

  const getCategoryInfo = (key: string, category: any): string => {
    const timeframeName = timeframe === '24h' ? '24 hours' : timeframe === '7d' ? '7 days' : '30 days';
    const infos: Record<string, string> = {
      authentication: `Over the last ${timeframeName}, we tracked ${category.events} authentication events with a security score of ${category.score}/100.${category.riskFactors?.timing > 0 ? ` ${category.riskFactors.timing} points of risk from unusual login times (outside 6am-10pm).` : ' All logins occurred during normal business hours.'}${category.riskFactors?.userBehavior > 0 ? ` ${category.riskFactors.userBehavior} failed login attempts detected.` : ' No failed login attempts.'}`,
      dataAccess: `Monitored ${category.events} data access events with a ${category.score}/100 security score.${category.riskFactors?.frequency > 0 ? ` Frequency risk: ${category.riskFactors.frequency} points (high volume detected).` : ' Access volume is within normal range.'}${category.riskFactors?.timing > 0 ? ` ${category.riskFactors.timing} points from after-hours access.` : ''}${category.riskFactors?.userBehavior > 0 ? ` ${category.riskFactors.userBehavior} failed access attempts.` : ''}`,
      activityMonitoring: `${category.events === 0 ? 'No suspicious activity detected - excellent!' : `Detected ${category.events} suspicious activities requiring attention.`} This category tracks unusual patterns like rapid requests, privilege escalation attempts, and anomalous behavior.`,
      userManagement: `${category.events} user management action${category.events !== 1 ? 's' : ''} in the last ${timeframeName}.${category.riskFactors?.frequency > 0 ? ` High frequency detected (${category.riskFactors.frequency} risk points).` : ' Activity level is normal.'}${category.events === 0 ? ' No user changes made during this period.' : ''}`,
      financial: `${category.events === 0 ? 'No financial transactions in this period.' : `Tracked ${category.events} financial activities with ${category.score}/100 security score.`} Monitors billing access, payment processing, and pricing modifications.`
    };
    return infos[key] || 'Security monitoring data for this category.';
  };

  const getRiskFactorColor = (value: number) => {
    if (value <= 10) return 'text-green-600';
    if (value <= 25) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRiskFactorLabel = (key: string) => {
    switch (key) {
      case 'frequency': return 'Event Frequency';
      case 'timing': return 'Unusual Timing';
      case 'userBehavior': return 'Failed Attempts';
      case 'geographic': return 'Geographic Risk';
      default: return key;
    }
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Loading security dashboard...</span>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Failed to Load Security Dashboard
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Unable to retrieve security information
          </p>
          <Button onClick={() => loadDashboard({ mode: 'initial' })} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Safety check for dashboard properties to prevent undefined errors
  if (!dashboard.status || !dashboard.categories || !dashboard.summary) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Incomplete Security Data
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Security dashboard data is incomplete. Please refresh to reload.
          </p>
          <Button onClick={() => loadDashboard({ mode: 'refresh' })} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Data
          </Button>
        </div>
      </div>
    );
  }

  const isDegraded = dashboard.status === 'degraded' || dashboard.metadata?.dataQuality === 'degraded';

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Security Events Dashboard</h2>
          <p className="text-sm text-gray-500">Real-time security monitoring and analysis</p>
        </div>
        <div className="flex items-center space-x-2">
          <select 
            value={timeframe} 
            onChange={(e) => setTimeframe(e.target.value as '24h' | '7d' | '30d')}
            className="px-3 py-1 border rounded-md text-sm"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <Button
            onClick={() => {
              if (!dashboard) return;
              runLexSecurityOverview(timeframe as LexSecurityTimeframe);
            }}
            variant="outline"
            size="sm"
            disabled={!dashboard || loading || refreshing}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Lex
          </Button>
          <Button 
            onClick={() => loadDashboard({ mode: dashboard ? 'refresh' : 'initial' })}
            variant="outline" 
            size="sm"
            disabled={loading || refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${(loading || refreshing) ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing' : 'Refresh'}
          </Button>
        </div>
      </div>

      {isDegraded && (
        <Card className="border border-amber-300 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-amber-900 dark:text-amber-200">
                  Security Telemetry Degraded
                </div>
                <div className="text-sm text-amber-800 dark:text-amber-300">
                  {dashboard.metadata?.message || 'Live security telemetry is temporarily unavailable. Values shown are fallback estimates until data access recovers.'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overall Security Status */}
      <Card className={`border-2 ${getStatusColor(dashboard.status)}`}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {getStatusIcon(dashboard.status)}
              <div>
                <CardTitle className="text-xl">
                  🛡️ SECURITY POSTURE: {(dashboard.status || 'unknown').toUpperCase().replace('_', ' ')}
                </CardTitle>
                <CardDescription className="flex items-center space-x-4 mt-1">
                  <span>{dashboard.summary?.totalEvents || 0} Activities Analyzed</span>
                  <span>•</span>
                  <span>Last Scan: {dashboard.summary?.lastActivity ? new Date(dashboard.summary.lastActivity).toLocaleTimeString() : 'Unknown'}</span>
                </CardDescription>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">
                {dashboard.summary?.riskScore || 0}/100
              </div>
              <div className="text-sm text-gray-600">Security Score</div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border">
              <div className="text-2xl font-bold text-lex-accent">
                {dashboard.summary?.totalEvents || 0}
              </div>
              <div className="text-xs text-gray-600">Total Events</div>
            </div>
            <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border">
              <div className="text-2xl font-bold text-green-600">
                {dashboard.categories ? Object.values(dashboard.categories).filter(c => c.status === 'good').length : 0}
              </div>
              <div className="text-xs text-gray-600">Categories Secure</div>
            </div>
            <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border">
              <div className="text-2xl font-bold text-yellow-600">
                {dashboard.categories ? Object.values(dashboard.categories).filter(c => c.status === 'warning').length : 0}
              </div>
              <div className="text-xs text-gray-600">Need Attention</div>
            </div>
            <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border">
              <div className="text-2xl font-bold text-red-600">
                {dashboard.categories ? Object.values(dashboard.categories).filter(c => c.status === 'critical').length : 0}
              </div>
              <div className="text-xs text-gray-600">Critical Issues</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Security Events (materialized by anomaly detectors) */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Recent Security Events
              </CardTitle>
              <CardDescription>
                Detected security anomalies and incidents for the selected timeframe.
              </CardDescription>
            </div>
            <Badge variant="secondary">
              {recentSecurityEventsTotal} total
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {recentSecurityEventsError ? (
            <div className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {recentSecurityEventsError}
            </div>
          ) : recentSecurityEvents.length === 0 ? (
            <div className="text-sm text-gray-600 dark:text-gray-300">
              No security events detected in this period.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Time
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Severity
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Source IP
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {recentSecurityEvents.map((evt) => {
                    const isExpanded = expandedSecurityEventId === evt.id;
                    const severityVariant =
                      evt.severity === 'critical' || evt.severity === 'high'
                        ? 'destructive'
                        : evt.severity === 'medium'
                          ? 'secondary'
                          : 'outline';
                    return (
                      <React.Fragment key={evt.id}>
                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">
                            {new Date(evt.timestamp).toLocaleString()}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <Badge variant={severityVariant as any}>
                              {evt.severity}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {evt.eventType}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">
                            {evt.sourceIp || '—'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">
                            {evt.investigationStatus}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setExpandedSecurityEventId(isExpanded ? null : evt.id)}
                              className="inline-flex items-center gap-2"
                            >
                              <Info className="w-4 h-4" />
                              {isExpanded ? 'Hide' : 'View'}
                            </Button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} className="px-4 py-3 bg-gray-50 dark:bg-gray-900/20">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                                    Threat Indicators
                                  </div>
                                  <div className="text-sm text-gray-800 dark:text-gray-100">
                                    {Array.isArray(evt.threatIndicators) && evt.threatIndicators.length > 0
                                      ? evt.threatIndicators.join(', ')
                                      : '—'}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                                    Event Data
                                  </div>
                                  <pre className="text-xs whitespace-pre-wrap break-words bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2 max-h-48 overflow-auto">
                                    {JSON.stringify(evt.eventData || {}, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security Categories - Object Structure */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dashboard.categories ? Object.entries(dashboard.categories).map(([key, category]) => (
          <Card key={key} className={`border ${getCategoryStatusColor(category.status)}`}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{category.name}</span>
                  <button
                    onClick={() => {
                      runLex(
                        {
                          type: 'security_category',
                          payload: { timeframe, categoryKey: key, category, dashboardSummary: dashboard.summary }
                        },
                        {
                          title: `Lex: ${category.name}`,
                          subtitle: `Timeframe: ${timeframe}`
                        }
                      );
                    }}
                    className="text-gray-400 hover:text-lex-accent transition-colors"
                    title="Lex explain"
                  >
                    <Sparkles className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowInfoModal(showInfoModal === key ? null : key)}
                    className="text-gray-400 hover:text-lex-accent transition-colors"
                    title="View details"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </div>
                <Badge variant={category.status === 'good' ? 'default' : category.status === 'warning' ? 'secondary' : 'destructive'}>
                  {category.status}
                </Badge>
              </CardTitle>
              <CardDescription>
                {getCategoryDescription(key)}
              </CardDescription>
              {showInfoModal === key && (
                <div className="mt-3 p-3 bg-lex-accent-soft border border-lex-accent rounded-md">
                  <div className="flex items-start space-x-2">
                    <Info className="w-4 h-4 text-lex-accent mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-lex-accent">
                      {getCategoryInfo(key, category)}
                    </p>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Score Display */}
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">
                    {category.score}
                  </div>
                  <div className="text-sm text-gray-500">Security Score</div>
                </div>

                {/* Events Count */}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Events:</span>
                  <span className="font-medium">{category.events}</span>
                </div>

                {/* Last Event */}
                {category.lastEvent && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Last Event:</span>
                    <span className="text-sm">{new Date(category.lastEvent).toLocaleString()}</span>
                  </div>
                )}

                {/* Risk Factors */}
                {category.riskFactors && (
                  <div className="pt-2 border-t">
                    <div className="text-xs font-medium text-gray-600 mb-2">Risk Factors:</div>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(category.riskFactors).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-xs">
                          <span>{getRiskFactorLabel(key)}:</span>
                          <span className={getRiskFactorColor(value)}>{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status Indicator */}
                <div className="pt-2 border-t">
                  <div className="flex items-center space-x-2">
                    {category.status === 'good' && <CheckCircle className="w-4 h-4 text-green-600" />}
                    {category.status === 'warning' && <Clock className="w-4 h-4 text-yellow-600" />}
                    {category.status === 'critical' && <AlertTriangle className="w-4 h-4 text-red-600" />}
                    <span className="text-sm">
                      {category.status === 'good' && 'All systems normal'}
                      {category.status === 'warning' && 'Monitoring required'}
                      {category.status === 'critical' && 'Immediate attention needed'}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )) : null}
      </div>

      {/* Alert Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Activity className="w-5 h-5 mr-2" />
            Alert Summary
          </CardTitle>
          <CardDescription>
            Current security alerts and their priorities
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {dashboard.alerts?.total || 0}
              </div>
              <div className="text-sm text-gray-500">Total Alerts</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {dashboard.alerts?.byPriority?.high || 0}
              </div>
              <div className="text-sm text-gray-500">High Priority</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {dashboard.alerts?.byPriority?.medium || 0}
              </div>
              <div className="text-sm text-gray-500">Medium Priority</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-lex-accent">
                {dashboard.alerts?.byPriority?.low || 0}
              </div>
              <div className="text-sm text-gray-500">Low Priority</div>
            </div>
          </div>
        </CardContent>
      </Card>

      

      {/* Last Refresh */}
      <div className="text-center text-sm text-gray-500">
        Monitoring {dashboard.summary?.totalEvents || 0} activities in the last {timeframe.replace('h', ' hours').replace('d', ' days')} • 
        Last updated: {lastRefresh.toLocaleTimeString()}{refreshing ? ' (updating…)': ''}
      </div>
    </div>

    <LexExplainModal
      isOpen={lexOpen}
      title={lexTitle}
      subtitle={lexSubtitle}
      explanation={lexExplanation}
      provider={lexProvider}
      model={lexModel}
      references={lexReferences as any}
      referenceLoading={lexReferenceLoading}
      loading={lexLoading}
      error={lexError}
      onClose={() => setLexOpen(false)}
      canBack={lexStack.length > 0}
      onBack={lexStack.length > 0 ? handleLexBack : undefined}
      onReferenceClick={(ref) => runLexSecurityReference(ref as LexSecurityReference)}
      onRetry={(lastLexRequest || lastSecurityOverviewTimeframe || lastSecurityReference) ? handleLexRetry : undefined}
    />
    </>
  );
};
